import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, ShieldCheck, Flask, Certificate, Truck, FilePdf, Vault, Lightning, Headset, Crown, ChartLineUp, Lock, SealCheck } from "@phosphor-icons/react";

const HERO = "/hero_banner.png";

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="border border-primary/25 bg-white/[0.04] p-8 hover:border-primary/60 transition-colors" style={{ transition: "border-color 0.3s" }}>
    <Icon size={30} weight="duotone" className="text-primary mb-5" />
    <h3 className="font-head text-lg mb-2 text-foreground">{title}</h3>
    <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
  </div>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [coaProducts, setCoaProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => {
      setFeatured(r.data.filter((p) => p.category !== "Starter Stacks").slice(0, 4));
      setCoaProducts(r.data.filter((p) => p.coa_path));
    }).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-black/10 bg-background overflow-hidden">
        <div className="relative">
          <img src={HERO} alt="Mulan Peptide Collection — Science you can reproduce" className="w-full h-auto block" />
          <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: "linear-gradient(0deg, #FFFDF7 5%, transparent 100%)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-2 pb-16 -mt-10 md:-mt-16 relative flex flex-wrap justify-center gap-4 fade-up">
          <Link to="/shop" data-testid="hero-shop-btn" className="group flex items-center gap-2 bg-primary text-white label-mono px-7 py-4 hover:bg-[#C9A227] transition-colors" style={{ transition: "background-color 0.2s" }}>
            Browse Catalog <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" style={{ transition: "transform 0.2s" }} />
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white/[0.03] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="mb-10">
            <span className="label-mono text-primary">Why Mulan</span>
            <h2 className="font-head text-3xl md:text-4xl mt-2">The Standard for Research Peptides</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Feature icon={Certificate} title="Third-Party Tested" desc="Every batch is independently verified by HPLC & mass spectrometry for identity and purity." />
            <Feature icon={ShieldCheck} title="≥99% Purity" desc="Rigorous quality control ensures consistent, reproducible research outcomes." />
            <Feature icon={Flask} title="Cold-Chain Handled" desc="Lyophilized and stored under optimal conditions to preserve peptide integrity." />
            <Feature icon={Truck} title="Discreet Shipping" desc="Fast, secure fulfillment with tracking on every research order." />
            <Feature icon={Lightning} title="Same-Day Shipping" desc="Orders placed before cutoff ship the same day so your research never waits." />
            <Feature icon={Headset} title="24/7 Support" desc="Our team is available around the clock to answer questions and resolve issues." />
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="label-mono text-primary">Collection</span>
            <h2 className="font-head text-3xl md:text-4xl mt-2">Featured Compounds</h2>
          </div>
          <Link to="/shop" data-testid="view-all-btn" className="label-mono text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
            View All <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* COA Vault */}
      <section id="coa-vault" className="max-w-7xl mx-auto px-6 pb-24" data-testid="coa-vault">
        <div className="border border-primary/25 bg-surface p-8 md:p-12">
          <div className="flex items-start gap-4 mb-8">
            <Vault size={34} weight="duotone" className="text-primary shrink-0 mt-1" />
            <div>
              <span className="label-mono text-primary">Certificates of Analysis</span>
              <h2 className="font-head text-3xl md:text-4xl mt-2">COA Vault</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">
                Download third-party HPLC & mass-spectrometry certificates verifying identity, net content, and purity for every documented compound.
              </p>
            </div>
          </div>

          {coaProducts.length === 0 ? (
            <div data-testid="coa-vault-empty" className="border border-black/10 bg-background p-10 text-center">
              <FilePdf size={36} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">
                Certificates are being added. Check back soon, or contact us for a specific lot COA.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {coaProducts.map((p) => (
                <a
                  key={p.id}
                  data-testid={`coa-vault-item-${p.id}`}
                  href={`${API}/coa/${p.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between gap-3 border border-black/10 bg-background px-5 py-4 hover:border-primary hover:bg-primary/5 transition-colors"
                  style={{ transition: "border-color 0.2s, background-color 0.2s" }}
                >
                  <div className="min-w-0">
                    <p className="font-head text-sm truncate">{p.name}</p>
                    <span className="label-mono text-muted-foreground text-[0.55rem]">{p.category}</span>
                  </div>
                  <span className="flex items-center gap-1 label-mono text-primary shrink-0">
                    <FilePdf size={16} weight="duotone" /> PDF
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quality Control */}
      <section className="border-y border-white/10" data-testid="quality-control">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <Crown size={40} weight="duotone" className="text-primary mx-auto mb-4" />
          <h2 className="font-head text-3xl md:text-5xl tracking-wide">QUALITY YOU CAN TRUST</h2>
          <p className="label-mono text-muted-foreground mt-3">Advanced Analytical Testing · Verified Purity · Complete Transparency</p>

          <div className="grid md:grid-cols-2 gap-5 mt-12 text-left">
            <div className="border border-primary/25 bg-white/[0.04] p-8">
              <ChartLineUp size={30} weight="duotone" className="text-primary mb-4" />
              <h3 className="font-head text-xl mb-3">HPLC Purity Analysis</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                High-performance liquid chromatography confirms exceptional purity and consistency in every batch, with results verified at ≥99%.
              </p>
            </div>
            <div className="border border-primary/25 bg-white/[0.04] p-8">
              <Flask size={30} weight="duotone" className="text-primary mb-4" />
              <h3 className="font-head text-xl mb-3">Mass Spectrometry Identity Confirmation</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Mass spectrometry verifies the exact molecular weight and identity of every peptide for accurate, reproducible research results.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8 text-left">
            <QualityBadge icon={ShieldCheck} title="Premium Quality" desc="Rigorously tested for purity and performance." />
            <QualityBadge icon={SealCheck} title="Laboratory Standards" desc="Testing performed using validated analytical methods." />
            <QualityBadge icon={Lock} title="Complete Transparency" desc="Detailed, verified results available for every lot." />
            <QualityBadge icon={Crown} title="Excellence in Every Batch" desc="Because your research deserves the best." />
          </div>
        </div>
      </section>
    </div>
  );
}

const QualityBadge = ({ icon: Icon, title, desc }) => (
  <div className="flex flex-col gap-2">
    <Icon size={24} weight="duotone" className="text-primary" />
    <h4 className="label-mono text-foreground">{title}</h4>
    <p className="text-muted-foreground text-xs leading-relaxed">{desc}</p>
  </div>
);
